import { useEffect, useState } from "react";
import api from "@/lib/api";

/** <img> for an authenticated backend endpoint (blob-fetched).
 *  Pass `body` to POST it — used to render the UNSAVED editor state. */
export function AuthImage({ path, alt, className, testId, refreshKey, body,
                            subtle = false, emptyLabel = "No artwork" }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let revoke = null;
    let alive = true;
    setError("");
    setUrl(null);
    if (!path) return undefined;
    (async () => {
      try {
        const token = localStorage.getItem("scwt_token");
        const r = await fetch(`${api.defaults.baseURL}${path}`, {
          method: body ? "POST" : "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            ...(body ? { "Content-Type": "application/json" } : {}),
          },
          body: body ? JSON.stringify(body) : undefined,
        });
        if (!r.ok) {
          let detail = "";
          try { detail = (await r.json()).detail; } catch { /* non-JSON */ }
          throw new Error(detail || `HTTP ${r.status}`);
        }
        const blob = await r.blob();
        if (!alive) return;
        revoke = URL.createObjectURL(blob);
        setUrl(revoke);
      } catch (e) {
        if (alive) setError(e.message || emptyLabel);
      }
    })();
    return () => {
      alive = false;
      if (revoke) URL.revokeObjectURL(revoke);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, refreshKey]);

  if (error) {
    return (
      <div className={`${className} flex items-center justify-center p-2 text-center text-[10px] font-semibold ${
        subtle ? "bg-slate-100 text-slate-400" : "bg-rose-50 text-rose-700"}`}
        data-testid={testId ? `${testId}-empty` : undefined}>
        {subtle ? emptyLabel : error}
      </div>
    );
  }
  if (!url) {
    return <div className={`${className} animate-pulse bg-slate-100`} data-testid={testId ? `${testId}-loading` : undefined} />;
  }
  return <img src={url} alt={alt} className={className} data-testid={testId} />;
}

export async function openAuthedPdf(path, body) {
  const token = localStorage.getItem("scwt_token");
  const r = await fetch(`${api.defaults.baseURL}${path}`, {
    method: body ? "POST" : "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) {
    let detail = "";
    try { detail = (await r.json()).detail; } catch { /* non-JSON */ }
    throw new Error(detail || `HTTP ${r.status}`);
  }
  const url = URL.createObjectURL(await r.blob());
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
